import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { PageHead, card, Field, inputCls, Empty } from "@/components/admin/kit";

function PilotRules({ rules, onSaved }) {
  const api = useApi();
  const [r, setR] = useState(rules);
  useEffect(() => { setR(rules); }, [rules]);
  const save = async () => {
    try { await api.put("/settings/rules", { quiet_start: r.quiet_start, quiet_end: r.quiet_end, max_calls_per_phone: Number(r.max_calls_per_phone), family_alerts_enabled: !!r.family_alerts_enabled }); toast.success("Call rules saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={card} data-testid="pilot-rules">
      <p className="font-bold">Pilot call rules</p>
      <p className="mt-1 text-xs text-muted-foreground">One call per dose time. No retries. Keys: 2 = replay, 9 = stop all calls.</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <Field label="Quiet hours start"><input type="time" className={inputCls} value={r.quiet_start} onChange={(e) => setR({ ...r, quiet_start: e.target.value })} data-testid="rules-quiet-start" /></Field>
        <Field label="Quiet hours end"><input type="time" className={inputCls} value={r.quiet_end} onChange={(e) => setR({ ...r, quiet_end: e.target.value })} data-testid="rules-quiet-end" /></Field>
        <Field label="Max calls / day / phone"><input type="number" min="1" max="12" className={inputCls} value={r.max_calls_per_phone} onChange={(e) => setR({ ...r, max_calls_per_phone: e.target.value })} data-testid="rules-max-calls" /></Field>
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!r.family_alerts_enabled} onChange={(e) => setR({ ...r, family_alerts_enabled: e.target.checked })} data-testid="rules-family-alerts" /> Family alerts (coming soon) <span className="text-xs text-muted-foreground">— off for the pilot. WhatsApp previews only, nothing is sent.</span></label>
      <Button className="mt-3" onClick={save} data-testid="rules-save-btn">Save rules</Button>
    </div>
  );
}

function CallPlan() {
  const [plan] = useLoad("/call-plan");
  return (
    <div className={card} data-testid="call-plan">
      <p className="font-bold">Calls per phone per day</p>
      <p className="mt-1 text-xs text-muted-foreground">All patients on one phone combined. Same time = one merged call.</p>
      <div className="mt-3 grid gap-2">
        {!plan?.length ? <Empty>No phones yet.</Empty> : plan.map((x, xi) => (
          <div key={`${x.phone}-${xi}`} className={`flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2 text-sm ${x.over_limit ? "bg-red-50" : "bg-[var(--lav)]"}`} data-testid={`plan-${xi}`}>
            <b>{x.phone}</b><span className="text-muted-foreground">{x.patients.join(", ")}</span>
            <span className="ml-auto text-xs">{x.times.join(" · ") || "no active doses"}</span>
            <span className={`rounded-full px-2 text-xs font-bold ${x.over_limit ? "bg-red-600 text-white" : "bg-white"}`}>{x.calls_per_day}/day</span>
            {x.quiet.length > 0 && <span className="rounded-full bg-orange-100 px-2 text-xs font-semibold text-orange-800">quiet hours: {x.quiet.join(", ")} (doctor set)</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

const PROVIDERS = [["simulator", "Simulator (no real calls)"], ["twilio", "Twilio"], ["exotel", "Exotel"]];

export default function Settings() {
  const api = useApi();
  const [s, reload] = useLoad("/settings");
  const [busy, setBusy] = useState(false);
  const setProvider = async (p) => {
    try { await api.put("/settings", { call_provider: p }); reload(); toast.success(`Call provider: ${p}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const gen = async () => {
    setBusy(true);
    try { const r = await api.post("/settings/generate-samples"); toast.success(`Website samples ready (${r.data.generated.length})`); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return null;
  return (
    <div data-testid="settings-page">
      <PageHead title="Settings" sub="Keys are stored securely on the server and never shown in full." />
      <div className="grid gap-4 lg:grid-cols-2">
        <PilotRules rules={s.rules} onSaved={reload} />
        <CallPlan />
        <div className={card}>
          <p className="mb-3 font-bold">Call provider</p>
          <div className="grid gap-2">
            {PROVIDERS.map(([k, l]) => {
              const ready = k === "simulator" || s.keys[k]?.ready;
              return (
                <button key={k} disabled={!ready} onClick={() => setProvider(k)} data-testid={`provider-${k}`}
                  className={`flex items-center gap-3 rounded-2xl border p-3 text-left text-sm transition-colors ${s.call_provider === k ? "border-[var(--violet)] bg-[var(--lav-2)]" : "border-[var(--lav-3)]"} disabled:opacity-50`}>
                  {s.call_provider === k ? <CheckCircle2 className="text-[var(--violet)]" size={18} /> : <Circle size={18} />}
                  <span className="font-semibold">{l}</span>{!ready && <span className="ml-auto text-xs text-muted-foreground">add keys first</span>}
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-xs text-muted-foreground" data-testid="exotel-setup-note">Exotel is built and switched off. To turn it on later: in Exotel create an App Bazaar flow — Gather applet (dynamic URL: <code>/api/webhooks/exotel/gather</code>) → Passthru applet (<code>/api/webhooks/exotel/passthru</code>; 200 → back to Gather, 302 → Hangup). Add the 6 Exotel keys (account SID, API key, API token, caller ID / ExoPhone, subdomain e.g. api.in.exotel.com, flow app ID), then pick Exotel here. The simulator always works.</p>
        </div>
        <div className={card}>
          <p className="mb-3 font-bold">API keys</p>
          {Object.entries(s.keys).map(([g, v]) => (
            <div key={g} className="mb-3" data-testid={`keys-${g}`}>
              <p className="text-sm font-semibold capitalize">{g} {v.ready ? <span className="text-green-700">· ready</span> : <span className="text-orange-700">· not set</span>}</p>
              {v.keys.map((k) => <p key={k.name} className="flex text-xs"><code>{k.name}</code><span className="ml-auto text-muted-foreground">{k.set ? k.hint || "set" : "—"}</span></p>)}
            </div>
          ))}
          <p className="text-xs text-muted-foreground">To add or change a key, ask in the Emergent chat — a secure pop-up collects it.</p>
        </div>
        <div className={`${card} lg:col-span-2`}>
          <p className="font-bold">Website sample calls</p>
          <p className="mt-1 text-sm text-muted-foreground">Generates the Telugu voice for the 3 public sample calls. Needs the Sarvam key. To change a sample's script or voice, use <a href="/admin/website-clips" className="font-semibold text-[var(--violet)] underline" data-testid="settings-clips-link">Website voice clips</a>.</p>
          <Button className="mt-3" onClick={gen} disabled={busy} data-testid="generate-samples-btn">{busy ? <Loader2 className="animate-spin" size={16} /> : <Mic size={16} />} Generate sample voices</Button>
          <ul className="mt-4 grid gap-2 text-sm" data-testid="sample-voice-settings">
            {(s.samples || []).map((x) => (
              <li key={x.key} className="rounded-2xl bg-[var(--lav)] px-3 py-2" data-testid={`sample-settings-${x.key}`}>
                <b>{x.title}</b> · {x.ready ? `${x.duration}s` : "not generated yet"}<br /><span className="text-xs text-muted-foreground">{x.settings || "—"}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
